import React, { useCallback, useEffect, useMemo } from 'react';
import { StyleSheet, View } from "react-native";
import { FlashList } from '@shopify/flash-list';
import ThemedDivider from "components/atoms/ThemedDivider";
import { listStyles } from 'styles/list'
import _ from 'lodash';
import { ThemedListStyles } from 'styles/components/atoms/ThemedList'
import HelperString from 'utils/helper-string'
import { layoutStyles } from 'styles/layout'

const ThemedList = ({ data = [], style = {}, scrollEnabled = false, dividerEnabled = true, renderItem, keyExtractor, keyExtractorItem, maxRenderSize = 15, estimatedItemSize = 100, numColumns = 1, onItemsVisible, ...listProps }) => {
	
	const hasOnItemsVisibleFunction = _.isFunction( onItemsVisible)
	
	// FlashList 2.0.2 spreads this prop into an object internally.
	const flatListStyle = StyleSheet.flatten([
		listStyles.listContainer,
		ThemedListStyles.listContainer,
		layoutStyles.flexOne,
		style,
	])
	
	const viewStyle = [
		listStyles.listContainer,
	]
	
	if( !_.isArray( data ) ){
		data = _.values( data );
	}
	
	if( !_.isFunction( keyExtractor ) ) {
		keyExtractor = ( item, index ) => {
			let key = keyExtractorItem ? _.get( item, keyExtractorItem, null ) : null;
			if( key === null ) {
				key = item?.guid ?? item?.id ?? item?.key ?? HelperString.hash( 'flat-list-item', index, item );
			}

			return String( key );
		}
	}

	const changeHandler = useCallback( ({ viewableItems }) => {
		if( hasOnItemsVisibleFunction ) {
			onItemsVisible( viewableItems )
		}
	}, [ hasOnItemsVisibleFunction, onItemsVisible ] );

	const onViewableItemsChanged = useMemo( () => {
		return _.debounce(changeHandler, 300);
	}, [ changeHandler ] );

	useEffect( () => {
		return () => {
			return onViewableItemsChanged.cancel();
		};
	}, [ onViewableItemsChanged ] );
	
	const size = _.size( data );
	
	useEffect( () => {
		if( !scrollEnabled && hasOnItemsVisibleFunction ) {
			onItemsVisible( _.map( data, o => {
				return {
					item: o
				}
			}) )
		}
	}, [ data, scrollEnabled ])
	
	// ScrollView not enabled? Do NOT use the flatview (errors with scrollview)
	if( !scrollEnabled ) {
		const content = []
		
		_.forEach( data, (item, key ) => {
			if( dividerEnabled && key > 0 ) {
				content.push(
					<ThemedDivider
						key={ HelperString.hash('themed-list-divider', key ) }
					/>
				)
			}
			
			content.push( renderItem({ item }) )
		});
		
		return (
			<View
				style={ viewStyle }>
				
				{ content }
			</View>
		)
	}
	
	return (
		<FlashList
			{ ...listProps }
			style={ flatListStyle }
			keyExtractor={ keyExtractor }
			ItemSeparatorComponent={ dividerEnabled ? ThemedDivider : null }
			scrollEventThrottle={ 16 }
			scrollEnabled={ scrollEnabled }
			data={ data }
			numColumns={ numColumns }
			renderItem={ renderItem }
			drawDistance={ estimatedItemSize * Math.min( size, maxRenderSize ) }
			onViewableItemsChanged={ onViewableItemsChanged }
		/>
	)
}

export default ThemedList
